import { cx } from '@flowus/common/cx';
import { IpcMessage } from '@flowus/shared';
import type { FC, MouseEvent } from 'react';
import { useEffect, useRef } from 'react';
import type { IconName } from 'src/common/components/icon';
import { Icon } from 'src/common/components/icon';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { listViewNormalClassName } from 'src/common/components/list-view/helper';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { SPECIAL_SPACE_ID } from 'src/common/const';
import { Import } from 'src/editor/editor/uikit/import';
import { lockScreen } from 'src/hooks/public/use-lock';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { useCurrentSpace } from 'src/hooks/space';
import { Modals } from 'src/modals';
import { ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { ipcOnMessage, isFlowUsApp } from 'src/utils/electron-util';
import { ShortcutOption, ShortcutSystemSymbol } from 'src/utils/shortcut';
import { useOpenSearchPopup } from '../header/search';
import { OpenSettingFrom, SettingMenuType } from '../setting-modal/type';
import { openSettingModal } from '../setting-modal/use-open-setting-modal';
import { MessagePanel } from './message-panel';
import { useUnreadState } from './message-panel/shared';
import { $subscribeUpdate } from './subscribe/store';

export const QuickSettings: FC<{}> = () => {
  const subscribeRef = useRef(null);
  const openModal = useOpenModal();
  const currentSpace = useCurrentSpace();
  const unreadSpace = useUnreadState();
  const unread = unreadSpace[currentSpace.uuid];
  const subscribeUnread = unreadSpace[SPECIAL_SPACE_ID];
  const spaceUnread =
    (unread?.inbox ?? 0) +
    (unread?.mention ?? 0) +
    (unread?.share ?? 0) +
    (subscribeUnread?.subscribe ?? 0);
  // const { showOnce: profitShowPoint } = useOnceAnyDay(OnceAnyDayIds.SettingProfitRedPoint, {
  //   endDay: -1,
  //   enable: !__PRIVATE__ && !__BUILD_IN__,
  // });
  const isGuest = useIsGuest();
  const openSearchPopup = useOpenSearchPopup();
  const subscribeUpdate = $subscribeUpdate();

  const openSetting = () => {
    bizTracker.event('setting');
    openSettingModal({
      defaultMenuType: SettingMenuType.space,
      from: OpenSettingFrom.package,
    });
  };

  const openMessageList = (event: MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'right-end',
      modalId: Modals.MESSAGE_NOTIFICATION,
      offset: [0, 8],
      content: () => <MessagePanel />,
    });
  };

  const openMore = (event: MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'bottom',
      content: (_) => (
        <ListView
          className={cx(listViewNormalClassName, 'w-48')}
          onItemClick={() => _.onCloseModal()}
          items={[
            __BUILD_IN__
              ? {
                  type: ListItemType.OPERATION,
                  isHidden: __PRIVATE__ || isGuest,
                  data: {
                    title: '我的订阅',
                    icon: 'IcSubscribe',
                    onClick: () => {
                      if (subscribeUpdate) {
                        $subscribeUpdate.setState(false);
                      }
                      window.open(ViewPath.subscribe);
                    },
                  },
                }
              : {
                  type: ListItemType.OPERATION,
                  isHidden: __PRIVATE__ || isGuest,
                  data: {
                    title: '导入文件',
                    icon: 'IcImport',
                    onClick: openImport,
                  },
                },
            {
              type: ListItemType.OPERATION,
              data: {
                title: '锁定屏幕',
                icon: 'IcLock',
                onClick: lockScreen,
                tip: `锁屏 ${ShortcutOption}+Shift+L`,
                placement: 'right',
              },
            },
          ]}
        />
      ),
    });
  };

  const openImport = () => {
    openModal.modal({
      content: () => <Import from="button" />,
    });
  };

  useEffect(() => {
    if (isFlowUsApp.check) {
      ipcOnMessage(IpcMessage.OnOpenSetting, openSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex justify-between mx-2 flex-shrink-0 px-2">
      {[
        {
          icon: 'IcSearch',
          popup: `快速查找文档或文件${ShortcutSystemSymbol}+P`,
          onClick: () => openSearchPopup(),
        },
        {
          icon: 'IcMessage',
          popup: '查看消息通知',
          className: 'message-panel',
          onClick: openMessageList,
          badge: !!spaceUnread && (
            <span className="absolute -top-1.5 -right-1 min-w-[20px] h-[18px] px-1 inline-flex items-center justify-center text-white bg-red text-t4-medium ml-auto rounded-3xl">
              {spaceUnread}
            </span>
          ),
        },
        __BUILD_IN__
          ? {
              icon: 'IcImport',
              popup: '导入文件',
              onClick: () => {
                openImport();
              },
              isHidden: __PRIVATE__ || isGuest,
            }
          : {
              icon: 'IcSubscribe',
              popup: '我的订阅',
              onClick: () => {
                if (subscribeUpdate) {
                  $subscribeUpdate.setState(false);
                }
                window.open(ViewPath.subscribe);
              },
              ref: subscribeRef,
              isHidden: __PRIVATE__,
              badge: subscribeUpdate ? (
                <span className="w-2 h-2 rounded absolute top-0 right-0 bg-red" />
              ) : null,
            },
        {
          icon: 'IcSettings',
          popup: '空间设置',
          onClick: openSetting,
          // badge: profitShowPoint && (
          //   <ProIcon text="💰赚佣金" className="-top-3 !rounded right-auto pointer-events-none" />
          // ),
        },
        {
          icon: 'IcMore',
          popup: '更多',
          onClick: openMore,
        },
      ].map((item, index) => {
        if (item.isHidden) return null;
        return (
          <div ref={item.ref} key={index}>
            <Tooltip
              popup={item.popup}
              key={item.icon}
              placement="bottom"
              onClick={item.onClick}
              className={cx(
                'relative flex items-center w-7 h-7 justify-center animate-hover text-grey2',
                item.className
              )}
            >
              <Icon name={item.icon as IconName} size="middle" />
              {item.badge}
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};
